/**
 * Spec 107 — PagePersonaFitCard.
 *
 * Decomposes a CONTENT-QUALITY-HYPOTHESIS page persona-fit evaluation into
 * priority / [wellbeing] / trust-provision / anti-signal rows, each with its
 * verbatim PAGE quote + reasoning. Shows the content-quality-hypothesis label,
 * a persona-confidence badge, and — mandatory — BOTH caveats (persona-definition
 * AND page-encounter). Informational personas render priority and wellbeing as
 * TWO DISTINCT blocks, never summed.
 *
 * Unevaluable evaluations render "couldn't evaluate — [reason]", NEVER a zero
 * (the load-bearing guard against a silent 0/0/0). This lens is never blended
 * with the GEO score (083) or the response persona-fit (106).
 *
 * data-testids (per spec input): page-persona-eval-view, ppe-dimension,
 * ppe-dimension-quote, ppe-content-quality-label, ppe-caveat-persona,
 * ppe-caveat-encounter, ppe-priority-block, ppe-wellbeing-block, ppe-unevaluable.
 */
import { useState } from 'react';
import { Icon } from './ui/Icon.js';
import { Row } from './ui/Row.js';
import { PersonaConfidenceBadge } from './PersonaConfidenceBadge.js';
import type {
  PageDimensionType,
  PagePersonaDimensionResult,
  PagePersonaEvaluation,
} from '../api/pagePersonaEvaluations.js';

const DIM_LABEL: Record<PageDimensionType, string> = {
  priority: 'Priority-fit',
  wellbeing: 'Wellbeing-fit',
  trust_provision: 'Trust (page provides evidence)',
  anti_signal: 'Anti-signal penalty',
};

function fmt(n: number | null): string {
  return n == null ? '—' : n.toFixed(2);
}

/**
 * Spec 215 — relative age. Mirrors the `timeAgo` shape already used by
 * ExecutionMatrix, CollectionStatus, PipelineSummaryBar and ProcessingStatus.
 * Kept local like those four rather than extracting a shared helper, which
 * would be a five-file refactor this spec did not ask for.
 */
function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * An absolute time that survives being read tomorrow in another timezone.
 * A bare wall-clock reading in a document that outlives its session is worse
 * than no timestamp, because it looks authoritative and is ambiguous.
 */
function absoluteTime(dateStr: string): string {
  const d = new Date(dateStr);
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `${d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} (${tz})`;
}

/**
 * The four readings this card must distinguish. Queued vs running is the
 * load-bearing one: it is the difference between "nothing is working on this"
 * and "something is, be patient", and it is invisible without `started_at`.
 */
function timingLabel(e: {
  evaluation_status: string;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}): string {
  const terminal = e.completed_at != null;
  if (terminal) {
    const verb = e.evaluation_status === 'failed' ? 'failed' : 'completed';
    return `${verb} ${absoluteTime(e.completed_at as string)} · ${timeAgo(e.completed_at)}`;
  }
  if (e.started_at == null) return `queued ${timeAgo(e.created_at)}`;
  return `running since ${timeAgo(e.started_at)}`;
}

function DimensionRow({
  type,
  score,
  results,
}: {
  type: PageDimensionType;
  score: number | null;
  results: PagePersonaDimensionResult[];
}) {
  const [open, setOpen] = useState(false);
  const ofType = results.filter((r) => r.dimension_type === type);
  const hasDetail = ofType.length > 0;
  return (
    <div data-testid="ppe-dimension" data-dimension={type} className="ppe-dim-row">
      <button
        type="button"
        data-testid="dimension-row-toggle"
        aria-expanded={open}
        className="ppe-dim-header group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-1"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="bx-chevron-right" className={`shrink-0 text-slate-400 transition-transform duration-150 group-hover:text-slate-500 ${
            open ? 'rotate-90' : ''
          }`} />
        <span className="flex-1">{DIM_LABEL[type]}</span>
        <span className="ppe-dim-score tabular-nums text-slate-900">{fmt(score)}</span>
      </button>
      {open && hasDetail && (
        <div className="ppe-dim-detail space-y-3 pl-7 pr-2 pt-2 pb-1">
          {ofType.map((r) => (
            <div key={r.id} className="space-y-1.5">
              {r.supporting_quote && (
                <figure className="m-0">
                  <figcaption className="mb-1 text-xs font-semibold text-slate-400">
                    What the page says
                  </figcaption>
                  <blockquote
                    data-testid="ppe-dimension-quote"
                    className="m-0 rounded-r-md border-l-[3px] border-amber-400 bg-amber-50/50 py-2 pl-3 pr-3 text-sm leading-relaxed text-slate-700"
                  >
                    “{r.supporting_quote}”
                  </blockquote>
                </figure>
              )}
              {r.reasoning && (
                <p className="m-0 text-sm leading-relaxed text-slate-600">
                  <span className="font-medium text-slate-500">Why it scored: </span>
                  {r.reasoning}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function PagePersonaFitCard({
  evaluation,
  canDelete = false,
  onDelete,
  onExport,
}: {
  evaluation: PagePersonaEvaluation;
  /** Spec 115 — admin-only delete affordance. */
  canDelete?: boolean;
  onDelete?: (id: string) => void | Promise<void>;
  /** Spec 215 — download this single evaluation as markdown. */
  onExport?: (id: string) => void | Promise<void>;
}) {
  const results = evaluation.dimension_results ?? [];
  const isInformational = evaluation.persona_type === 'informational';
  const isUnevaluable = evaluation.evaluation_status === 'unevaluable';
  const caveats = evaluation.caveats;

  // Spec 115 — two-step delete confirm (never one-click; destructive + irreversible).
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await onDelete?.(evaluation.id);
    } finally {
      setDeleting(false);
      setConfirming(false);
    }
  };

  return (
    // Spec 235 — disclosure shape: no evaluation has a detail page, and this
    // card's own actionable regions are the four independent DimensionRow
    // toggles below plus export/delete, never a "click card to navigate"
    // gesture. `to` is unconditionally omitted, so there is never a stretched
    // ::after here — unlike ProposalCard/PromptsPage's mixed cases, none of
    // this card's nested buttons need .hub-row__interactive, because the
    // overlay they'd need lifting above never exists in the first place.
    <Row
      data-testid="page-persona-eval-view"
      title={evaluation.page_url}
      top={
        <>
          {/* Content-quality-hypothesis label — the lens's claim type. */}
          <span
            data-testid="ppe-content-quality-label"
            className="ppe-content-quality-label inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 border border-amber-200"
            title="A content-quality hypothesis about the page; never blended with the GEO score or response persona-fit"
          >
            Content-quality hypothesis · page persona-fit
          </span>
          {/* Persona-definition caveat (inherited confidence badge). */}
          <span data-testid="ppe-confidence-badge">
            <PersonaConfidenceBadge confidence={evaluation.persona_confidence_at_eval} />
          </span>
          <span className="text-xs text-slate-500">
            persona v{evaluation.persona_version}
          </span>
          <span className="text-xs text-slate-500">
            status: {evaluation.evaluation_status}
          </span>
          {/* Spec 215 — WHEN, chosen by state rather than one field printed
              unconditionally. A completion time alone cannot answer the question
              that produced this: an evaluation sat at "pending" and nobody could
              tell whether it was stuck, precisely because an unfinished
              evaluation has no completion time. `started_at === null` is the
              whole signal — it separates "the worker never picked this up" from
              "the model is taking a while". */}
          <span className="hub-row__time" data-testid="ppe-timing">
            {timingLabel(evaluation)}
          </span>
        </>
      }
      actions={
        <>
          {/* Spec 215 — export THIS evaluation as markdown. Offered only once the
              evaluation is terminal: a document with score-shaped blanks reads as a
              measurement of zero rather than the absence of one, so an unfinished
              evaluation has nothing to export and the server refuses it too. */}
          {onExport && evaluation.completed_at != null && (
            <button
              type="button"
              data-testid="ppe-export-md"
              onClick={() => void onExport(evaluation.id)}
              className="text-xs text-slate-500 underline underline-offset-2 hover:text-slate-800"
              title="Download this evaluation as a markdown file"
            >
              Download .md
            </button>
          )}

          {/* Spec 115 — admin delete (two-step confirm; removes dimension results, irreversible). */}
          {canDelete && (
            confirming ? (
              <>
                <span className="text-xs text-slate-500">Delete? Can’t be undone.</span>
                <button
                  type="button"
                  data-testid="ppe-delete-confirm"
                  className="rounded-md bg-rose-600 px-2 py-0.5 text-xs font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                  disabled={deleting}
                  onClick={() => void handleDelete()}
                >
                  {deleting ? 'Deleting…' : 'Confirm'}
                </button>
                <button
                  type="button"
                  className="hub-btn hub-btn--ghost"
                  disabled={deleting}
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type="button"
                data-testid="ppe-delete"
                className="rounded-md p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                title="Delete this page persona-fit evaluation (removes its dimension results; cannot be undone)"
                aria-label="Delete this page persona-fit evaluation"
                onClick={() => setConfirming(true)}
              >
                <Icon name="bx-trash" />
              </button>
            )
          )}
        </>
      }
    >
      {/* BOTH caveats — mandatory (FR-6). */}
      <div className="ppe-caveats space-y-1 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 mb-2.5">
        <p
          data-testid="ppe-caveat-persona"
          className="text-xs text-amber-800 m-0"
        >
          {caveats?.persona_definition_caveat ??
            'Persona-definition caveat: inherits the uncertainty of the persona definition.'}
        </p>
        <p
          data-testid="ppe-caveat-encounter"
          className="text-xs text-amber-800 m-0"
        >
          {caveats?.page_encounter_caveat ??
            'Page-encounter caveat: the persona may never directly see this raw page.'}
        </p>
      </div>

      {isUnevaluable ? (
        <p
          data-testid="ppe-unevaluable"
          className="text-sm font-semibold text-amber-700"
        >
          Couldn’t evaluate —{' '}
          {evaluation.unevaluable_reason ?? 'page yielded no usable content'}. This
          is not a score of 0; the page could not be assessed.
        </p>
      ) : evaluation.evaluation_status === 'completed' ? (
        <div className="ppe-dimensions space-y-3">
          {/* Priority block (always). */}
          <div data-testid="ppe-priority-block">
            <DimensionRow
              type="priority"
              score={evaluation.page_priority_fit_score}
              results={results}
            />
          </div>
          {/* Wellbeing block — informational only, SEPARATE from priority. */}
          {isInformational && (
            <div data-testid="ppe-wellbeing-block">
              <DimensionRow
                type="wellbeing"
                score={evaluation.page_wellbeing_fit_score}
                results={results}
              />
            </div>
          )}
          <DimensionRow
            type="trust_provision"
            score={evaluation.trust_provision_score}
            results={results}
          />
          <DimensionRow
            type="anti_signal"
            score={evaluation.anti_signal_penalty}
            results={results}
          />
        </div>
      ) : (
        <p className="text-sm text-slate-500">Evaluation {evaluation.evaluation_status}…</p>
      )}
    </Row>
  );
}
